import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { dashboardApi, usersApi, gamesApi } from '../services/api';

function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [shortages, setShortages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [lateCheckins, setLateCheckins] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [assigning, setAssigning] = useState(false);

  const openAssignModal = async (shortage) => {
    setAssignModal(shortage);
    setSelectedDealer('');
    if (dealers.length === 0) {
      try {
        const res = await usersApi.getDealers();
        setDealers(res.data?.dealers || res.dealers || []);
      } catch (e) { console.error(e); }
    }
  };

  const handleAssignDealer = async () => {
    if (!selectedDealer || !assignModal) return;
    setAssigning(true);
    try {
      await gamesApi.assignDealerToTable(assignModal.gameId, selectedDealer, 1);
      setAssignModal(null);
      fetchDashboardData();
    } catch (e) {
      alert(e.message || 'Failed to assign dealer');
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, shortagesRes, activityRes, lateRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getShortages().catch(() => ({ data: { shortages: [] } })),
        dashboardApi.getActivity().catch(() => ({ data: { activities: [] } })),
        dashboardApi.getLateCheckins().catch(() => ({ data: { lateCheckins: [] } })),
      ]);
      setStats(statsRes.data || statsRes);
      setShortages(shortagesRes.data?.shortages || []);
      setActivity(activityRes.data?.activities || []);
      setLateCheckins(lateRes.data?.lateCheckins || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getOrdinal = (n) => {
    if (n === 1) return '1st';
    if (n === 2) return '2nd';
    if (n === 3) return '3rd';
    return `${n}th`;
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="main-content">
          <div className="loading"><div className="loading-spinner"></div></div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <h2>Dashboard</h2>
          <p>Welcome back, {user?.firstName || user?.username}!</p>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-value">{stats?.totalPlayers || 0}</div>
            <div className="stat-label">Total Players</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎴</div>
            <div className="stat-value">{stats?.totalGames || 0}</div>
            <div className="stat-label">Total Games</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-value">{stats?.upcomingGames || 0}</div>
            <div className="stat-label">Upcoming</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{stats?.completedGames || 0}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📍</div>
            <div className="stat-value">{stats?.totalVenues || 0}</div>
            <div className="stat-label">Venues</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-value">{stats?.totalLeagues || 0}</div>
            <div className="stat-label">Leagues</div>
          </div>
        </div>

        {/* Dealer Shortages Alert */}
        {shortages.length > 0 && (
          <div className="card" style={{ marginTop: 20, borderLeft: '4px solid #e74c3c' }}>
            <div className="card-header">
              <h3>⚠️ Dealer Shortages — Next 7 Days</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm" onClick={async () => {
                  if (!window.confirm(`Auto-fill all ${shortages.length} shortages using rotation?`)) return;
                  try {
                    const r = await dashboardApi.autoFillShortages();
                    alert(`Assigned ${r.data?.assigned || 0} dealers`);
                    fetchDashboardData();
                  } catch (e) { alert(e.message); }
                }}>🔄 Auto-Fill</button>
                <span className="badge badge-warning">{shortages.length} {shortages.length === 1 ? 'game' : 'games'}</span>
              </div>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Venue</th>
                  <th>Tables</th>
                  <th>Dealers</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shortages.slice(0, 8).map(s => (
                  <tr key={s.gameId} onClick={() => openAssignModal(s)} style={{ cursor: 'pointer' }}>
                    <td>{formatDateTime(s.startTime)}</td>
                    <td><strong>{s.venueName}</strong></td>
                    <td>{s.tablesNeeded}</td>
                    <td>{s.dealersAssigned}/{s.tablesNeeded}</td>
                    <td>
                      <span className={`badge ${s.status === 'critical' ? 'badge-danger' : 'badge-warning'}`}>
                        {s.status === 'critical' ? 'No dealer' : `Short ${s.shortfall}`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Late Check-ins */}
        {lateCheckins.length > 0 && (
          <div className="card" style={{ marginTop: 20, borderLeft: '4px solid #f39c12' }}>
            <div className="card-header">
              <h3>⏰ Late Check-ins — Dealers Not Checked In</h3>
              <span className="badge badge-warning">{lateCheckins.length}</span>
            </div>
            <table className="data-table">
              <thead>
                <tr><th>Game Time</th><th>Dealer</th><th>Phone</th><th>Venue</th></tr>
              </thead>
              <tbody>
                {lateCheckins.map(l => (
                  <tr key={l.gameId + l.dealerId}>
                    <td>{formatDateTime(l.startTime)}</td>
                    <td><strong>{l.dealerName}</strong></td>
                    <td>{l.phone || '—'}</td>
                    <td>{l.venueName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Activity Feed */}
        {activity.length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header">
              <h3>📡 Recent Activity</h3>
              <span style={{ color: '#888', fontSize: 13 }}>Last 24 hours</span>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {activity.map((a, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <div style={{ fontSize: 24 }}>
                    {a.type === 'checkin' ? '📍' : a.type === 'elimination' ? '🏆' : '👤'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong>{a.actor}</strong> {a.text}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {new Date(a.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
          {/* Top Players */}
          <div className="card">
            <div className="card-header">
              <h3>Top Players</h3>
              <Link to="/promotions" className="btn btn-secondary btn-sm">View Leaderboard</Link>
            </div>
            {(stats?.topPlayers || []).length === 0 ? (
              <div className="empty-state" style={{ padding: '30px 0' }}>
                <div className="empty-state-icon">🏆</div>
                <p>No results recorded yet</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th>Points</th>
                    <th>Games</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.topPlayers || []).map(player => (
                    <tr key={player.rank}>
                      <td><strong>{player.rank <= 3 ? ['🥇','🥈','🥉'][player.rank - 1] : player.rank}</strong></td>
                      <td><strong>{player.firstName} {player.lastName}</strong></td>
                      <td>{player.totalPoints}</td>
                      <td>{player.gamesPlayed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent Games */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Games</h3>
              <Link to="/games" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            {(stats?.recentGames || []).length === 0 ? (
              <div className="empty-state" style={{ padding: '30px 0' }}>
                <div className="empty-state-icon">🎴</div>
                <p>No games yet</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Venue</th>
                    <th>Date</th>
                    <th>Players</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.recentGames || []).map(game => (
                    <tr key={game.id}>
                      <td><strong>{game.venueName || '-'}</strong></td>
                      <td>{formatDateTime(game.startTime)}</td>
                      <td>{game.playerCount}/{game.maxPlaces}</td>
                      <td>
                        <span className={`badge ${game.status === 'completed' ? 'badge-success' : new Date(game.startTime) > new Date() ? 'badge-info' : 'badge-warning'}`}>
                          {game.status === 'completed' ? 'Done' : new Date(game.startTime) > new Date() ? 'Upcoming' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Player Growth Chart */}
        {(stats?.playerGrowth || []).length > 0 && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Player Growth (Last 12 Weeks)</h3>
            </div>
            <div style={{ padding: '20px', display: 'flex', alignItems: 'flex-end', gap: '4px', height: '160px' }}>
              {(stats?.playerGrowth || []).map((week, i) => {
                const max = Math.max(...stats.playerGrowth.map(w => w.newPlayers), 1);
                const height = Math.max((week.newPlayers / max) * 120, 4);
                const weekDate = new Date(week.week);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontSize: '11px', color: '#666' }}>{week.newPlayers}</span>
                    <div style={{ width: '100%', height: `${height}px`, background: 'linear-gradient(180deg, #2563eb, #1d4ed8)', borderRadius: '4px 4px 0 0' }} title={`Week of ${weekDate.toLocaleDateString()}: ${week.newPlayers} new players`} />
                    <span style={{ fontSize: '9px', color: '#999', whiteSpace: 'nowrap' }}>
                      {weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <Link to="/games" className="btn btn-primary"><span>🎴</span> Manage Games</Link>
            <Link to="/venues" className="btn btn-primary"><span>📍</span> Manage Venues</Link>
            <Link to="/users" className="btn btn-primary"><span>👥</span> Manage Users</Link>
            <Link to="/promotions" className="btn btn-primary"><span>🏅</span> Promotions</Link>
            <Link to="/leagues" className="btn btn-primary"><span>🏆</span> Leagues</Link>
            <Link to="/reports" className="btn btn-primary"><span>📋</span> Reports</Link>
            <Link to="/kiosk" className="btn btn-secondary"><span>🖥️</span> Kiosk Mode</Link>
          </div>
        </div>

        {/* Assign Dealer Modal */}
        {assignModal && (
          <div className="modal-overlay" onClick={() => setAssignModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Assign Dealer</h3>
                <button className="modal-close" onClick={() => setAssignModal(null)}>&times;</button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 16 }}>
                  <strong>{assignModal.venueName}</strong><br />
                  {formatDateTime(assignModal.startTime)}
                </p>
                <div className="form-group">
                  <label>Select Dealer</label>
                  <select
                    className="form-select"
                    value={selectedDealer}
                    onChange={e => setSelectedDealer(e.target.value)}
                  >
                    <option value="">— Choose a dealer —</option>
                    {dealers.map(d => (
                      <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setAssignModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleAssignDealer} disabled={!selectedDealer || assigning}>
                  {assigning ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
